/**********************************************************************
 * LiveProgramCard.jsx
 * – Card (live / prossimamente) con barra di progresso + PRIORITY badge
 *********************************************************************/
import React, { useState } from 'react';
import '../../../css/liveProgramCard.css';
import PersonDetailModal from '../People/PersonDetailModal';
import api from '../../../services/api';

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */
const LiveProgramCard = ({ data, progress, expanded, onToggleExpanded, onOpenSchedule }) => {
  const [detailPerson, setDetailPerson] = useState(null);

  const handlePersonClick = async (person) => {
    try {
      // Fetch della persona completa con qualifications
      const response = await api.get(`/people/${person.id}`)
      setDetailPerson(response.data)
    } catch (error) {
      console.error('Error loading person details:', error)
      // Fallback: usa i dati disponibili
      setDetailPerson(person)
    }
  }
  
  /* ------------------ data sanity / normalizzazione ---------------- */
  if (!data || !data.program) return null;

  const { program, schedule, isLive } = data;
  const prog     = schedule.programData || program;
  const priority = schedule.priority ?? 0;
  const slots    = schedule.activeSlots || schedule.timeSlots || [];

  const groupCompanyPeople = (slot) => {
    const companyGroups = (slot.companyPeople || []).reduce((acc, link) => {
      const companyId = link.company?.id || link.companyId;
      if (!companyId) return acc;
      if (!acc[companyId]) {
        acc[companyId] = { company: link.company, people: [] };
      }
      if (link.person) {
        acc[companyId].people.push(link.person);
      }
      return acc;
    }, {});
    return Object.values(companyGroups);
  };

  /* --------------------- helper date / time ------------------------ */
  const fmt = t => (t?.length > 5 ? t.slice(0, 5) : t || '');

  const getScheduleDateTime = () => {
    if (!schedule.date || !schedule.startTime) return null;
    const time = fmt(schedule.startTime);
    return new Date(`${schedule.date}T${time}:00`);
  };
  const schDateTime = getScheduleDateTime();
  const now         = new Date();

  /* Se non è live e (per one-shot) l’orario è già passato → skip card */
  if (!isLive && schDateTime && schDateTime <= now) return null;

  /* ----------------------------- render ---------------------------- */
  return (
    <div
      className={
        `live-program-slider-card live-program-slider-live-card ` +
        (isLive ? 'live-program-slider-actually-live' : '')
      }
      onClick={onToggleExpanded}
    >
      {/* HEADER ------------------------------------------------------ */}
      <div
        className="live-program-slider-header"
        style={{
          backgroundImage: `url(${
            prog.imageUrl || prog.image_url || 'https://via.placeholder.com/300'
          })`,
        }}
      >
        <div className="live-program-slider-header-overlay">
          <div className="live-program-slider-header-left">
            <h2>{prog.title}</h2>
            <span className="live-program-slider-time-compact">
              {fmt(schedule.startTime)} - {fmt(schedule.endTime)}
              {schedule.isRecurring && schedule.dayOfWeek && ` · ${schedule.dayOfWeek}`}
            </span>
          </div>

          {/* BADGE container (priority + stato) ---------------------- */}
          <div className="badge-container">
            <span className="badge-priority">P{priority}</span>
            {isLive ? (
              <span className="live-program-slider-live-badge">LIVE</span>
            ) : (
              <span className="live-program-slider-upcoming-badge">
                Prossimamente
              </span>
            )}
            <button
              type="button"
              className="schedule-detail-btn"
              onClick={(e) => { e.stopPropagation(); onOpenSchedule?.(); }}
              title="Dettagli scheduling"
            >
              ⓘ
            </button>
          </div>
        </div>

        {/* Progress bar in overlay */}
        <div className="live-program-slider-progress-overlay">
          <div
            className={
              'live-program-slider-progress-bar ' +
              (isLive
                ? 'live-program-slider-progress-live'
                : 'live-program-slider-progress-upcoming')
            }
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* EXPANDED ---------------------------------------------------- */}
      {expanded && (
        <div className="live-program-slider-expanded-section">
          <h3>Dettagli Programma</h3>
          <p>{prog.description}</p>

          {prog.expectedGuests?.length > 0 && (
            <PivotSection
              title="Ospiti Programma"
              items={prog.expectedGuests}
              onPersonClick={handlePersonClick}
            />
          )}

          {schedule.guests?.length > 0 && (
            <PivotSection title="Ospiti Scheduling" items={schedule.guests} onPersonClick={handlePersonClick} />
          )}

          {prog.topics?.length > 0 && (
            <PivotSection title="Topics Programma" items={prog.topics} topic />
          )}

          {schedule.topics?.length > 0 && (
            <PivotSection title="Topics Scheduling" items={schedule.topics} topic />
          )}

          {schedule.description && (
            <div className="live-program-slider-schedule-extra">
              <h4>Descrizione Scheduling</h4>
              <p>{schedule.description}</p>
            </div>
          )}

          {slots.length > 0 && (
            <div className="live-program-slider-slots">
              <h4>Fasce Orarie</h4>
              {slots.map((slot, idx) => (
                <div key={slot.id || idx} className="live-program-slider-slot-card">
                  <div className="live-program-slider-slot-header">
                    <strong>{slot.startTime?.slice(0, 5)} - {slot.endTime?.slice(0, 5)}</strong>
                    {slot.isEditorial && <span className="badge-editorial">Redazionale</span>}
                  </div>
                  {slot.note && <p className="live-program-slider-slot-note">{slot.note}</p>}
                  {Array.isArray(slot.people) && slot.people.length > 0 && (
                    <>
                      <h5>Ospiti</h5>
                      <div className="live-program-slider-chip-container">
                        {slot.people.map(person => (
                          <div
                            key={person.id}
                            className="live-program-slider-chip"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePersonClick(person);
                            }}
                            style={{ cursor: 'pointer' }}
                            title="Clicca per visualizzare i dettagli"
                          >
                            <img src={person.imageUrl || person.image_url || 'https://via.placeholder.com/30'} alt="" />
                            <span>{person.nome} {person.cognome}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {Array.isArray(slot.topics) && slot.topics.length > 0 && (
                    <>
                      <h5>Argomenti</h5>
                      <div className="live-program-slider-chip-container">
                        {slot.topics.map(topic => (
                          <div key={topic.id} className="live-program-slider-chip live-program-slider-topic-chip">
                            <span>{topic.name}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {groupCompanyPeople(slot).length > 0 && (
                    <>
                      <h5>Aziende</h5>
                      <div className="live-program-slider-company-list">
                        {groupCompanyPeople(slot).map((group, companyIdx) => (
                          <div key={group.company?.id || `company-${companyIdx}`} className="live-program-slider-company">
                            <strong>{group.company?.name || 'Azienda'}</strong>
                            <div className="live-program-slider-chip-container">
                              {group.people.map(person => (
                                <div
                                  key={person.id}
                                  className="live-program-slider-chip"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePersonClick(person);
                                  }}
                                  style={{ cursor: 'pointer' }}
                                  title="Clicca per visualizzare i dettagli"
                                >
                                  <img src={person.imageUrl || person.image_url || 'https://via.placeholder.com/30'} alt="" />
                                  <span>{person.nome} {person.cognome}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {detailPerson && (
        <PersonDetailModal
          person={detailPerson}
          onClose={() => setDetailPerson(null)}
        />
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Sub-section (chips list)                                            */
/* ------------------------------------------------------------------ */
const PivotSection = ({ title, items, topic = false, onPersonClick = null }) => (
  <div className="live-program-slider-pivot-section">
    <h4>{title}</h4>
    <div className="live-program-slider-chip-container">
      {items.map(it => (
        <div
          key={it.id}
          className={
            'live-program-slider-chip' +
            (topic ? ' live-program-slider-topic-chip' : '')
          }
          onClick={(e) => {
            if (!topic && onPersonClick) {
              e.stopPropagation();
              onPersonClick(it);
            }
          }}
          style={!topic && onPersonClick ? { cursor: 'pointer' } : undefined}
          title={!topic && onPersonClick ? "Clicca per visualizzare i dettagli" : undefined}
        >
          {topic ? null : (
            <img
              src={it.imageUrl || it.image_url || 'https://via.placeholder.com/30'}
              alt=""
            />
          )}
          <span>{it.nome ? `${it.nome} ${it.cognome}` : it.name}</span>
        </div>
      ))}
    </div>
  </div>
);

export default LiveProgramCard;
